/**
 * Sound Effects Utility
 *
 * Generates engaging sound effects using the Web Audio API
 * No external audio files needed - all sounds are synthesized
 *
 * Usage:
 * import { playFlipSound, playCorrectSound } from './utils/soundEffects';
 * playFlipSound();
 */

// Create audio context (lazy initialization)
let audioContext = null;

function getAudioContext() {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
}

/**
 * Play a card flip sound
 * Enhanced whoosh with reverb
 */
export function playFlipSound() {
  try {
    const ctx = getAudioContext();

    // Create multiple oscillators for richer sound
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gainNode = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc1.type = 'sine';
    osc2.type = 'triangle';
    filter.type = 'lowpass';
    filter.frequency.value = 2000;

    osc1.connect(filter);
    osc2.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(ctx.destination);

    // Descending whoosh
    osc1.frequency.setValueAtTime(600, ctx.currentTime);
    osc1.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.15);
    osc2.frequency.setValueAtTime(800, ctx.currentTime);
    osc2.frequency.exponentialRampToValueAtTime(300, ctx.currentTime + 0.15);

    gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);

    osc1.start(ctx.currentTime);
    osc2.start(ctx.currentTime);
    osc1.stop(ctx.currentTime + 0.15);
    osc2.stop(ctx.currentTime + 0.15);
  } catch (error) {
    console.warn('Could not play flip sound:', error);
  }
}

/**
 * Play a "correct" or "easy" rating sound
 * Uplifting two-tone chime
 */
export function playCorrectSound() {
  try {
    const ctx = getAudioContext();

    // First tone
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.connect(gain1);
    gain1.connect(ctx.destination);

    osc1.frequency.value = 523.25; // C5
    gain1.gain.setValueAtTime(0.15, ctx.currentTime);
    gain1.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);

    osc1.start(ctx.currentTime);
    osc1.stop(ctx.currentTime + 0.2);

    // Second tone (higher)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.connect(gain2);
    gain2.connect(ctx.destination);

    osc2.frequency.value = 659.25; // E5
    gain2.gain.setValueAtTime(0.15, ctx.currentTime + 0.1);
    gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);

    osc2.start(ctx.currentTime + 0.1);
    osc2.stop(ctx.currentTime + 0.3);
  } catch (error) {
    console.warn('Could not play correct sound:', error);
  }
}

/**
 * Play a "good" rating sound
 * Single pleasant tone
 */
export function playGoodSound() {
  try {
    const ctx = getAudioContext();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.frequency.value = 587.33; // D5
    gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.2);
  } catch (error) {
    console.warn('Could not play good sound:', error);
  }
}

/**
 * Play a "hard" rating sound
 * Lower, cautious tone
 */
export function playHardSound() {
  try {
    const ctx = getAudioContext();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.frequency.value = 392.00; // G4
    gainNode.gain.setValueAtTime(0.12, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.25);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.25);
  } catch (error) {
    console.warn('Could not play hard sound:', error);
  }
}

/**
 * Play an "again" (incorrect) sound
 * Descending tone to indicate mistake
 */
export function playAgainSound() {
  try {
    const ctx = getAudioContext();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.frequency.setValueAtTime(349.23, ctx.currentTime); // F4
    oscillator.frequency.exponentialRampToValueAtTime(293.66, ctx.currentTime + 0.15); // D4

    gainNode.gain.setValueAtTime(0.12, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.15);
  } catch (error) {
    console.warn('Could not play again sound:', error);
  }
}

/**
 * Play a swipe sound
 * Quick swoosh effect
 */
export function playSwipeSound() {
  try {
    const ctx = getAudioContext();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.type = 'sawtooth';
    oscillator.frequency.setValueAtTime(200, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.08);

    gainNode.gain.setValueAtTime(0.08, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.08);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.08);
  } catch (error) {
    console.warn('Could not play swipe sound:', error);
  }
}

/**
 * Play session complete sound
 * Rich celebratory ascending melody with harmonics
 */
export function playSessionCompleteSound() {
  try {
    const ctx = getAudioContext();
    const melody = [
      { freq: 523.25, duration: 0.2 },  // C5
      { freq: 659.25, duration: 0.2 },  // E5
      { freq: 783.99, duration: 0.2 },  // G5
      { freq: 1046.50, duration: 0.4 }  // C6
    ];

    melody.forEach((note, index) => {
      const startTime = ctx.currentTime + (index * 0.15);

      // Main tone
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = note.freq;

      osc.connect(gain);
      gain.connect(ctx.destination);

      gain.gain.setValueAtTime(0.2, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + note.duration);

      osc.start(startTime);
      osc.stop(startTime + note.duration);

      // Add harmonic for richness
      const harmonic = ctx.createOscillator();
      const harmonicGain = ctx.createGain();
      harmonic.type = 'triangle';
      harmonic.frequency.value = note.freq * 2;

      harmonic.connect(harmonicGain);
      harmonicGain.connect(ctx.destination);

      harmonicGain.gain.setValueAtTime(0.08, startTime);
      harmonicGain.gain.exponentialRampToValueAtTime(0.01, startTime + note.duration);

      harmonic.start(startTime);
      harmonic.stop(startTime + note.duration);
    });
  } catch (error) {
    console.warn('Could not play session complete sound:', error);
  }
}

/**
 * Play navigation sound (next card)
 * Subtle ascending chirp
 */
export function playNextSound() {
  try {
    const ctx = getAudioContext();

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc.type = 'sine';
    filter.type = 'lowpass';
    filter.frequency.value = 3000;

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    // Quick ascending chirp
    osc.frequency.setValueAtTime(400, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.1);

    gain.gain.setValueAtTime(0.08, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.1);
  } catch (error) {
    console.warn('Could not play next sound:', error);
  }
}

/**
 * Play navigation sound (previous card)
 * Subtle descending chirp
 */
export function playPreviousSound() {
  try {
    const ctx = getAudioContext();

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc.type = 'sine';
    filter.type = 'lowpass';
    filter.frequency.value = 3000;

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    // Quick descending chirp
    osc.frequency.setValueAtTime(600, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.1);

    gain.gain.setValueAtTime(0.08, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.1);
  } catch (error) {
    console.warn('Could not play previous sound:', error);
  }
}

// === Quiz Battle Sounds ===

/**
 * Play quiz correct answer sound
 * Bright ascending arpeggio
 */
export function playQuizCorrectSound() {
  try {
    const ctx = getAudioContext();
    const notes = [523.25, 659.25, 783.99]; // C5, E5, G5
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(ctx.destination);
      const t = ctx.currentTime + i * 0.06;
      gain.gain.setValueAtTime(0.18, t);
      gain.gain.exponentialRampToValueAtTime(0.01, t + 0.15);
      osc.start(t);
      osc.stop(t + 0.15);
    });
  } catch (e) {
    console.warn('Could not play quiz correct sound:', e);
  }
}

/**
 * Play quiz wrong answer sound
 * Low buzzer with dissonance
 */
export function playQuizWrongSound() {
  try {
    const ctx = getAudioContext();
    [196, 185].forEach((freq) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.25);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.25);
    });
  } catch (e) {
    console.warn('Could not play quiz wrong sound:', e);
  }
}

/**
 * Play timer tick sound (final 5 seconds)
 * Sharp click
 */
export function playTimerTickSound() {
  try {
    const ctx = getAudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 880;
    osc.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0.12, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.05);
  } catch (e) {
    console.warn('Could not play timer tick sound:', e);
  }
}

/**
 * Play quiz victory fanfare
 * Triumphant ascending melody with harmonics
 */
export function playQuizVictorySound() {
  try {
    const ctx = getAudioContext();
    const melody = [
      { freq: 523.25, delay: 0 },     // C5
      { freq: 659.25, delay: 0.12 },   // E5
      { freq: 783.99, delay: 0.24 },   // G5
      { freq: 1046.50, delay: 0.36 },  // C6
      { freq: 1318.51, delay: 0.52 },  // E6
    ];
    melody.forEach(({ freq, delay }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(ctx.destination);
      const t = ctx.currentTime + delay;
      gain.gain.setValueAtTime(0.2, t);
      gain.gain.exponentialRampToValueAtTime(0.01, t + 0.3);
      osc.start(t);
      osc.stop(t + 0.3);

      // Harmonic
      const h = ctx.createOscillator();
      const hg = ctx.createGain();
      h.type = 'triangle';
      h.frequency.value = freq * 2;
      h.connect(hg);
      hg.connect(ctx.destination);
      hg.gain.setValueAtTime(0.06, t);
      hg.gain.exponentialRampToValueAtTime(0.01, t + 0.25);
      h.start(t);
      h.stop(t + 0.25);
    });
  } catch (e) {
    console.warn('Could not play quiz victory sound:', e);
  }
}

/**
 * Resume audio context (needed for some browsers after user interaction)
 */
export function resumeAudioContext() {
  if (audioContext && audioContext.state === 'suspended') {
    audioContext.resume();
  }
}
